import React, {Component} from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import {bindActionCreators} from "redux";
import {Link} from "react-router-dom";
import axios from "axios";
import ListView from "../common/ListView";
import {searchProducts, updateProductVisibility, showDeleteProductModal} from "./actions";
import {getCategories} from "../Category/actions";
import {getBrands} from "../Brand/actions";
import {getSuppliers} from "../Supplier/actions";
import {Button, Icon, notification, Dropdown, Menu} from "antd";
import DeleteProductFormModal from "./DeleteProductFormModal";
import MoneyDisplay from "../common/MoneyDisplay";
import {authorizationHeaders} from "../utils/api";

const hiddenStyle = {textDecoration: 'line-through'};

class ProductsPage extends Component {
  static propTypes = {
    /* state */
    history: PropTypes.object.isRequired,
    dispensaryId: PropTypes.string.isRequired,
    search: PropTypes.object.isRequired,
    categories: PropTypes.array.isRequired,
    brands: PropTypes.array.isRequired,
    suppliers: PropTypes.array.isRequired,
    deleteProductModalVisible: PropTypes.bool.isRequired,
    /* actions */
    searchProducts: PropTypes.func.isRequired,
    getCategories: PropTypes.func.isRequired,
    getBrands: PropTypes.func.isRequired,
    getSuppliers: PropTypes.func.isRequired,
    updateProductVisibility: PropTypes.func.isRequired,
    showDeleteProductModal: PropTypes.func.isRequired,
  };

  componentDidMount() {
    this.load();
  }

  componentDidUpdate(prevProps) {
    if (this.props.dispensaryId !== prevProps.dispensaryId) this.load();
  }

  load = () => {
    const {dispensaryId, getBrands, getCategories, getSuppliers} = this.props;
    getBrands({dispensaryId});
    getCategories({dispensaryId});
    getSuppliers({dispensaryId});
  };

  onVisibilityClick = async(values) => {
    try {
      await this.props.updateProductVisibility(values);
      this.listView.refresh();
    } catch (err) {
      notification.error({
        message: 'Failed changing visibility',
        description: err.message,
      });
    }
  };

  onExportAsCSVClick = async() => {
    const {dispensaryId} = this.props;
    // This is a hack so we get feedback from the client
    // don't even bother doing proper redux
    axios({
      url: `${process.env.PUBLIC_URL}/api/v1/r/products?filter[where][dispensaryId]=${dispensaryId}`,
      method: 'GET',
      headers: authorizationHeaders(),
      responseType: 'json', // important
    }).then(response => {
      if (!Array.isArray(response.data) || response.data.length === 0) return;
      // massage data into csv
      const keys = Object.keys(response.data[0]).filter(x => !x.endsWith("Id"));
      keys.sort();
      const header = keys.map(camelCaseToWords).join(";");
      const lines = [header];
      for (let row of response.data) {
        const line = keys.map(k => safeCSV(row[k])).join(";");
        lines.push(line);
      }
      return lines.join("\n");
    }).then((csv) => {
      if (!csv) return;
      // download csv
      const url = window.URL.createObjectURL(new Blob([csv]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'products.csv');
      document.body.appendChild(link);
      link.click();
    });

  };

  render() {
    const {
      dispensaryId, history, searchProducts, categories, brands, suppliers, deleteProductModalVisible,
      search: {searching, pagination, filters, sorter, results, searchFilter},
    } = this.props;
    const columns = [
      {
        title: 'Product',
        dataIndex: 'display',
        key: 'display',
        fixed: 'left',
        sorter: true,
      },
      {
        title: 'Description',
        dataIndex: 'description',
        key: 'description',
        sorter: true,
        className: 'truncated-column',
      },
      {
        title: 'Category',
        dataIndex: 'categoryName',
        key: 'categoryId',
        sorter: true,
        filters: categories.map(({categoryId: value, name: text}) => ({text, value})),
        render: (categoryName, {categoryVisible}) =>
          <span
            {...(categoryVisible === false ? {title: 'This category is hidden', style: hiddenStyle} : {})}
          >
            {categoryName}
          </span>
      },
      {
        title: 'Brand',
        dataIndex: 'brandName',
        key: 'brandId',
        sorter: true,
        filters: brands.map(({brandId: value, name: text}) => ({text, value})),
        render: (brandName, {brandVisible}) =>
          <span
            {...(brandVisible === false ? {title: 'This brand is hidden', style: hiddenStyle} : {})}
          >
            {brandName}
          </span>
      },
      {
        title: 'Supplier',
        dataIndex: 'supplierName',
        key: 'supplierId',
        sorter: true,
        filters: suppliers.map(({supplierId: value, name: text}) => ({text, value})),
      },
      {
        title: 'Visibility',
        key: 'visibility',
        render: (value, {productId, visibleOnTill, categoryVisible, brandVisible}) =>
          <Icon
            type="shopping-cart"
            style={{color: categoryVisible && brandVisible && visibleOnTill ? 'green' : 'red', cursor: 'pointer'}}
            onClick={() => categoryVisible && brandVisible
              ? this.onVisibilityClick({productId, visibleOnTill: !visibleOnTill})
              : notification.warning({message: 'Invalid action', description: 'Category or brand not visible'})
            }
          />
      },
      {
        title: 'Cost Price',
        dataIndex: 'costPrice',
        key: 'costPrice',
        sorter: true,
        render: price => <MoneyDisplay amount={price} decimals={5}/>
      },
      {
        title: 'Sale Price',
        dataIndex: 'salePrice',
        key: 'salePrice',
        sorter: true,
        render: price => <MoneyDisplay amount={price}/>
      },
      {
        title: 'Quantity In Stock',
        dataIndex: 'quantityInStock',
        key: 'quantityInStock',
        sorter: true,
      },
      {
        title: 'Quantity On Order',
        dataIndex: 'quantityInOrder',
        key: 'quantityInOrder',
        sorter: true,
      },
      {
        title: 'Quantity Returned',
        dataIndex: 'returns',
        key: 'returns',
        sorter: true,
      },
      {
        title: 'Actions',
        key: 'action',
        fixed: 'right',
        width: 170,
        render: (_, record) => {
          const encodedId = encodeURIComponent(record.productId);
          return (
            <React.Fragment>
              <Link to={`/product/update/${encodedId}`}>Edit</Link>
              {' | '}
              <Link to={`/product/stocks/${encodedId}`}>Stock</Link>
              {' | '}
              <Dropdown overlay={
                <Menu>
                  <Menu.Item key="0"><Link to={`/product/change-price/${encodedId}`}>Change Price</Link></Menu.Item>
                  <Menu.Item key="1"><Link to={`/product/history/${encodedId}`}>History</Link></Menu.Item>
                  <Menu.Divider/>
                  <Menu.Item key="2"><a onClick={() => this.props.showDeleteProductModal(record)}>Delete</a></Menu.Item>
                </Menu>
              } trigger={['click']}>
                <a className="ant-dropdown-link">
                More <Icon type="down" />
                </a>
              </Dropdown>
            </React.Fragment>
          );
        }
      }
    ];
    return <React.Fragment>
      {deleteProductModalVisible && <DeleteProductFormModal onDelete={() => this.listView.refresh()} />}
      <ListView
        ref={ref => this.listView = ref}
        title="Products"
        searchPlaceholder="Search name, description, brand, category, supplier and barcode"
        loading={searching}
        columns={columns}
        dataSourceProvider={searchProducts}
        dataSource={results}
        pagination={{...pagination, position: 'bottom'}}
        filters={filters}
        sorter={sorter}
        searchFilter={searchFilter}
        dispensaryId={dispensaryId}
        rowKey="productId"
        onAdd={() => history.push('/product/add')}
        scroll={{x: 1800}}
        footer={(currentPageData) => currentPageData.length && <Button onClick={this.onExportAsCSVClick}>Export as CSV</Button>}
      />
    </React.Fragment>;
  }
}

const mapStateToProps = state => ({
  dispensaryId: state.dispensary.selectedDispensaryId,
  search: state.product.productsSearch,
  categories: state.category.categories,
  brands: state.brand.brands,
  suppliers: state.supplier.suppliers,
  deleteProductModalVisible: state.product.deleteProductModalVisible,
});

const mapDispatchToProps = dispatch => (bindActionCreators({
  searchProducts,
  getCategories,
  getBrands,
  getSuppliers,
  updateProductVisibility,
  showDeleteProductModal,
}, dispatch));

export default connect(mapStateToProps, mapDispatchToProps)(ProductsPage);

function camelCaseToWords(cc) {
  const words = [];
  let word = '';
  for (let i=0; i<cc.length; i++) {
    if (cc[i] >= 'A' && cc[i] <= 'Z' && word) {
      words.push(word);
      word = cc[i];
    } else {
      word += cc[i];
    }
  }
  if (word) words.push(word);
  const result = words.join(' ');
  return result[0].toUpperCase() + result.substr(1);
}

function safeCSV(value) {
  if (typeof value === 'boolean') return value ? "Yes" : "No";
  if (typeof value === 'number') return value.toString();
  if (typeof value === 'string' && value) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return ""; //TODO object (including null), undefined end up here
}



// WEBPACK FOOTER //
// ./src/Product/ProductsPage.js